import SelectorEngine from '../../utils/dom/selector-engine';
import EventHandler from '../../utils/dom/event-handler';
import BaseComponent from '../BaseComponent';
import CourtAvaibilityPagination from '../CourtAvaibilityPagination';
import { timeConversion } from '../../utils/date';
import { KeyCode } from '../../utils/constants';
import { ClassNames } from '../../utils/dom/css-classes';

const SELECTOR_AVAIBILITY_DIV = '.js-court-avaibility-table';
const SELECTOR_NOT_AVAILABLE_DIV = '.js-court-avaibility-message';

const SELECTOR_PAGINATION_DIV = '.court-avaibility-pagination';
const SELECTOR_PAGINATION_DESKTOP = '.court-avaibility-pagination__desktop';
const SELECTOR_PAGINATION_TABLET = '.court-avaibility-pagination__tablet';
const SELECTOR_PAGINATION_MOBILE = '.court-avaibility-pagination__mobile';

const CLICK_EVENT = 'click';
const KEYPRESS = 'keypress';

const MOBILE_COLUMN = 1;
const TABLET_COLUMN = 3;
const DESKTOP_COLUMN = 4;

const DEFAULT_INTERVAL_MINUTES = 60;
const SLOT_INTERVAL_DEVIDER = 2;

const TABLE_CELL_HEIGHT = 36;

const SELECTED_DATA_START_TIME = '06:00';
const SELECTED_DATA_END_TIME = '18:00';

const NAME = 'CourtAvailability';

const SECOND = 1000;
const ONE_MINUTE_IN_MILLIS = SECOND * 60;

const IntervalTypes = {
	increment: 'increment',
	decrement: 'decrement'
};

export const filterArrayByOdd = (array) => {
	return array.filter((_, index) => index % 2 === 0);
};

const sortSlotsAscending = (currentSlot, nextSlot) => currentSlot.start - nextSlot.start;

class CourtAvailability extends BaseComponent {
	constructor(element) {
		super(element);
		if (this._element) {
			this._mainSelector = element;
			this._venueDetails = [];
			this._timeSlots = [];
			this._currentIndex = 0;
			this._selectedData = {
				slotInterval: DEFAULT_INTERVAL_MINUTES,
				startTime: SELECTED_DATA_START_TIME,
				endTime: SELECTED_DATA_END_TIME
			};
			this._slotCallbacks = [];

			this.init();
		}
	}

	static get NAME() {
		return NAME;
	}

	get config() {
		return this._config;
	}

	set config(config) {
		this._config = config;
		this._selectedData = {
			slotInterval: config.slotInterval || DEFAULT_INTERVAL_MINUTES,
			selectedDate: config.selectedDate,
			slots: config.slots,
			startTime: this.timeToTimeStamp(config.selectedDate, config.startTime),
			endTime: this.timeToTimeStamp(config.selectedDate, config.endTime)
		};
		this.setTimeSlotData();
	}

	get venueDetails() {
		return this._venueDetails;
	}

	set venueDetails(venueDetails = {}) {
		this._venueDetails = venueDetails;
		this.courtAvailabilityData = this.formatVenueDetails(this._venueDetails);
		this.setDataPagination(this._currentIndex, this.courtAvailabilityData);
		this.updateCourtsData(this.courtAvailabilityData, this._currentIndex);
	}

	get currentIndex() {
		return this._currentIndex;
	}

	set currentIndex(index) {
		this._currentIndex = index;
		this.setDataPagination(index, this.courtAvailabilityData);
		this.updateCourtsData(this.courtAvailabilityData, this._currentIndex);
	}

	init() {
		if (!this._mainSelector) {
			return;
		}
		this._wrapper = SelectorEngine.findOne(SELECTOR_AVAIBILITY_DIV, this._mainSelector);
		this._notAvailableWrapper = SelectorEngine.findOne(SELECTOR_NOT_AVAILABLE_DIV, this._mainSelector);

		this.paginationSelector = SelectorEngine.findOne(SELECTOR_PAGINATION_DIV, this._mainSelector);
		this.courtAvaibilityPaginationDesktopElement = SelectorEngine.findOne(
			SELECTOR_PAGINATION_DESKTOP,
			this.paginationSelector
		);
		this.courtAvaibilityPaginationTabletElement = SelectorEngine.findOne(
			SELECTOR_PAGINATION_TABLET,
			this.paginationSelector
		);
		this.courtAvaibilityPaginationMobileElement = SelectorEngine.findOne(
			SELECTOR_PAGINATION_MOBILE,
			this.paginationSelector
		);

		this.courtAvaibilityPaginationDesktop = new CourtAvaibilityPagination(this.courtAvaibilityPaginationDesktopElement);
		this.courtAvaibilityPaginationTablet = new CourtAvaibilityPagination(this.courtAvaibilityPaginationTabletElement);
		this.courtAvaibilityPaginationMobile = new CourtAvaibilityPagination(this.courtAvaibilityPaginationMobileElement);

		this.courtAvaibilityPaginationDesktop.onPrevious(() => {
			this.previousButtonClick(DESKTOP_COLUMN);
		});
		this.courtAvaibilityPaginationDesktop.onNext(() => {
			this.nextButtonClick(DESKTOP_COLUMN);
		});

		this.courtAvaibilityPaginationTablet.onPrevious(() => {
			this.previousButtonClick(TABLET_COLUMN);
		});
		this.courtAvaibilityPaginationTablet.onNext(() => {
			this.nextButtonClick(TABLET_COLUMN);
		});

		this.courtAvaibilityPaginationMobile.onPrevious(() => {
			this.previousButtonClick(MOBILE_COLUMN);
		});
		this.courtAvaibilityPaginationMobile.onNext(() => {
			this.nextButtonClick(MOBILE_COLUMN);
		});
	}

	timeToTimeStamp(date, timeStamp) {
		const timeData = timeStamp.toString().split(':');
		const selectedDate = new Date(date);
		selectedDate.setHours(timeData[0]);
		selectedDate.setMinutes(timeData[1]);
		return selectedDate;
	}

	updateStartTimeSlots() {
		const startTimeDate = new Date(this._selectedData.startTime);

		const startSlot = this._selectedData.slots[0];
		const startSlotDate = new Date(startSlot);

		if (startSlotDate > startTimeDate) {
			const updatedStartDate = this.updateInterval(startSlotDate, IntervalTypes.decrement);

			this._selectedData.slots = [updatedStartDate, ...this._selectedData.slots];

			this.updateStartTimeSlots();
		}
	}

	updateEndTimeSlots() {
		const endTimeDate = new Date(this._selectedData.endTime);

		const endSlot = this._selectedData.slots.pop();
		const endSlotDate = new Date(endSlot);

		if (endSlotDate < endTimeDate) {
			const updatedEndDate = this.updateInterval(endSlotDate, IntervalTypes.increment);

			this._selectedData.slots = [...this._selectedData.slots, updatedEndDate];

			this.updateEndTimeSlots();
		}
	}

	setTimeSlotData() {
		this.updateStartTimeSlots();

		this.updateEndTimeSlots();

		this._timeSlots = this._selectedData.slots.map((slot) => {
			return new Date(slot);
		});

		this._timeSlotView = this._timeSlots;
	}

	setDataPagination(index, courtData) {
		this.courtAvaibilityPaginationDesktop.paginationData = {
			currentIndex: index,
			pagination: DESKTOP_COLUMN,
			courtData: courtData
		};
		this.courtAvaibilityPaginationTablet.paginationData = {
			currentIndex: index,
			pagination: TABLET_COLUMN,
			courtData: courtData
		};
		this.courtAvaibilityPaginationMobile.paginationData = {
			currentIndex: index,
			pagination: MOBILE_COLUMN,
			courtData: courtData
		};
	}

	previousButtonClick(column) {
		if (this.currentIndex > 0) {
			if (this.currentIndex - column < 0) {
				this.currentIndex = 0;
			} else {
				this.currentIndex -= column;
			}
		}
	}

	nextButtonClick(column) {
		if (this.currentIndex + column <= this._venueDetails.length - 1) {
			this.currentIndex += column;
		}
	}

	updateInterval(date, type) {
		const interval = this._selectedData.slotInterval;

		if (!(date instanceof Date)) return date;

		if (type === IntervalTypes.increment) {
			date.setTime(date.getTime() + interval * ONE_MINUTE_IN_MILLIS);
		}

		if (type === IntervalTypes.decrement) {
			date.setTime(date.getTime() - interval * ONE_MINUTE_IN_MILLIS);
		}

		return date;
	}

	formatVenueDetails(venueDetails = []) {
		const courts = [];
		for (const venueDetail of venueDetails) {
			const slots = [];
			for (const slotData of venueDetail.availableSlots) {
				const selectedStartTime = this._selectedData.startTime;
				const startDateTime = new Date(slotData.startTime);

				const selectedEndTime = this._selectedData.endTime;
				const endDateTime = new Date(slotData.endTime);

				const isAvailableSlot = startDateTime >= selectedStartTime && endDateTime <= selectedEndTime;

				//Add only slots starting inside the opening/closing time interval
				if (isAvailableSlot) {
					const timeDuration = Math.abs(endDateTime - startDateTime);
					const timeDurationMinutes = Math.floor(timeDuration / ONE_MINUTE_IN_MILLIS);
					const slotDetails = {
						start: startDateTime,
						end: endDateTime,
						startTime: startDateTime,
						endTime: endDateTime,
						price: slotData.price,
						duration: timeDurationMinutes,
						id: slotData.slotId,
						courtID: venueDetail.id,
						courtName: venueDetail.name
					};
					slots.push(slotDetails);
				}
			}

			const court = {
				name: venueDetail.name,
				type: venueDetail.type,
				surface: venueDetail.surface,
				slotsDetails: slots.sort(sortSlotsAscending)
			};
			courts.push(court);
		}
		return courts;
	}

	updateCourtsData(courtData, currentIndex) {
		let lastIndex = currentIndex + DESKTOP_COLUMN;

		if (lastIndex > courtData.length - 1) {
			lastIndex = courtData.length;
		}

		const loadCourtData = [];
		for (let indexData = currentIndex; indexData < lastIndex; indexData++) {
			loadCourtData.push(courtData[indexData]);
		}

		this._wrapper.innerHTML = this.renderCourtsData(loadCourtData);
		this.slotsData = SelectorEngine.find('.court-avaibility-slot-book', this._wrapper);

		for (const availableSlot of this.slotsData) {
			const link = SelectorEngine.findOne('a', availableSlot);
			if (link) {
				EventHandler.on(link, CLICK_EVENT, this.availableSlotClickHandler.bind(this));
				EventHandler.on(link, KEYPRESS, (event) => {
					if (event.keyCode == KeyCode.Enter) {
						event.preventDefault();
						this.availableSlotClickHandler(event);
					}
				});
			}
		}
	}

	availableSlotClickHandler(event) {
		for (const callback of this._slotCallbacks) {
			callback(event);
		}
	}

	onSlotClick(callback) {
		this._slotCallbacks.push(callback);
	}

	renderCourtsData(courtData) {
		const timeSlotData = this._timeSlotView
			.map((rowData, index) => this.renderTimeSlotsRowData(rowData, index, this._timeSlotView))
			.join('');

		const headingData = courtData.map((rowData, index) => this.renderCourtsHeadingRow(rowData, index)).join('');
		const courtAvaibilityDetails = courtData
			.map((rowData, index) => this.renderCourtsAvailabilityData(rowData, index))
			.join('');
		return `
			<div class="court-avaibility-data u-border u-rounded md:u-border-0">
				<div class="court-avaibility-details-heading-row u-hstack u-text-center u-border-bottom u-hidden md:u-flex">
					<div class="court-avaibility-timeslot-column"></div>
					<div class="court-avaibility-details-data u-hstack u-mr-0 md:u-mr-3">
						${headingData}
					</div>
				</div>
				<div class="court-avaibility-details-row u-hstack">
					<div class="court-avaibility-timeslot-column">
						${timeSlotData}
					</div>
					<div class="court-avaibility-details-data u-hstack u-mr-0 md:u-mr-3">
						${courtAvaibilityDetails}
					</div>
				</div>
			</div>
		`;
	}
	renderCourtsHeadingRow(courtRowData, index) {
		let classesData = 'u-hidden';
		if (index < MOBILE_COLUMN) {
			classesData = 'sm:u-flex';
		} else if (MOBILE_COLUMN <= index && index < TABLET_COLUMN) {
			classesData = 'sm:u-hidden md:u-flex';
		} else if (TABLET_COLUMN <= index && index < DESKTOP_COLUMN) {
			classesData = 'sm:u-hidden md:u-hidden xl:u-flex';
		}
		return `
			<div class="court-avaibility-details-column u-mb-2 ${classesData}">
				<div class="u-caption1 u-mb-1">${courtRowData.name}</div>
				<div class="u-body2">${courtRowData.type}</div>
				<div class="u-body2">${courtRowData.surface} court</div>
			</div>
		`;
	}

	renderTimeSlotsRowData(timeSlot, index, timeSlotData) {
		return index > 0 && index < timeSlotData.length
			? `<div class="court-avaibility-timeslot u-border-end u-caption2">
      <span>${timeConversion(timeSlot)}</span>
			</div>`
			: '';
	}

	renderCourtsAvailabilityData(courtRowData, index) {
		const courtslotDetails =
			courtRowData.slotsDetails.length > 0
				? courtRowData.slotsDetails
						.map((rowData, indexdata) =>
							this.renderCourtsAvailabilityRowData(rowData, indexdata, courtRowData.slotsDetails)
						)
						.join('')
				: this._timeSlots.length > 0 && this.renderBookedSlot(this._timeSlots.length);

		let classesData = ClassNames.Hidden;
		if (index < MOBILE_COLUMN) {
			classesData = 'sm:u-flex';
		} else if (MOBILE_COLUMN <= index && index < TABLET_COLUMN) {
			classesData = 'sm:u-hidden md:u-flex';
		} else if (TABLET_COLUMN <= index && index < DESKTOP_COLUMN) {
			classesData = 'sm:u-hidden md:u-hidden xl:u-flex';
		}
		return `
			<div class="court-avaibility-details-column ${classesData}">
				${courtslotDetails}
			</div>
		`;
	}

	renderCourtsAvailabilityRowData(currentSlot, index, allSlots) {
		let startBookedSlot = '';
		let endBookedSlot = '';
		let middleBookedSlot = '';
		const previousSlot = allSlots[index - 1];

		if (index === 0) {
			const courtAvailableStartTime = allSlots[0].startTime;

			const startTimeSlotPosition = this._timeSlots.findIndex(
				(slot) => new Date(slot).getTime() === new Date(courtAvailableStartTime).getTime()
			);

			if (startTimeSlotPosition > 0) {
				startBookedSlot = this.renderBookedSlot(startTimeSlotPosition);
			}
		}

		if (index === 0) {
			middleBookedSlot = this.renderAvailableSlot(currentSlot);
		} else {
			if (previousSlot?.endTime === currentSlot.startTime) {
				middleBookedSlot = this.renderAvailableSlot(currentSlot);
			} else {
				const availableSlotIndexInAllSlots = this._timeSlots.findIndex(
					(day) => new Date(day).getTime() === new Date(currentSlot.startTime).getTime()
				);
				const previousAvailableSlotIndexInAllSlots = this._timeSlots.findIndex(
					(day) => new Date(day).getTime() === new Date(previousSlot.endTime).getTime()
				);

				const middleBookedSlotPosition = availableSlotIndexInAllSlots - previousAvailableSlotIndexInAllSlots;
				if (middleBookedSlotPosition > 0) {
					middleBookedSlot = this.renderBookedSlot(middleBookedSlotPosition);
				}
				middleBookedSlot += this.renderAvailableSlot(currentSlot);
			}
		}
		if (!!previousSlot && index === allSlots.length - 1) {
			const previousAvailableSlotIndexInAllSlots = this._timeSlots.findIndex(
				(day) => new Date(day).getTime() === new Date(allSlots[index].endTime).getTime()
			);

			if (previousAvailableSlotIndexInAllSlots > 0) {
				const endTimeSlotPosition = this._timeSlots.length - previousAvailableSlotIndexInAllSlots;

				if (endTimeSlotPosition > 0) {
					endBookedSlot = this.renderBookedSlot(endTimeSlotPosition);
				}
			}
		}
		return `
			${startBookedSlot}
			${middleBookedSlot}
			${endBookedSlot}
		`;
	}

	renderBookedSlot(timeSlotPosition) {
		return `
			<div
				class="court-avaibility-slot-book u-caption2 u-bg-grey-light u-border-end u-border-bottom u-text-center u-text-grey-dark u-vstack"
				style="height:${timeSlotPosition * TABLE_CELL_HEIGHT * SLOT_INTERVAL_DEVIDER}px">
				<span>Not available</span>
			</div>`;
	}

	renderAvailableSlot(slotData) {
		const slotPrice = `<br /> £${Number.parseFloat(slotData.price).toFixed(2)}`;

		const convertedStartTime = timeConversion(slotData.startTime);
		const convertedEndTime = timeConversion(slotData.endTime);

		const slotInterval = this._selectedData.slotInterval / SLOT_INTERVAL_DEVIDER;

		return `
			<div
				class="court-avaibility-slot-book u-link2 u-bg-background-green u-border-end u-border-bottom u-text-center u-vstack"
				style="height:${(slotData.duration / slotInterval) * TABLE_CELL_HEIGHT}px">
				<a
					data-slot-id="${slotData.id}"
					data-court-id="${slotData.courtID}"
					tabindex="0"
					aria-label="${
						slotData.courtName
					} ${convertedStartTime} - ${convertedEndTime} ${slotPrice}">${convertedStartTime} - ${convertedEndTime} ${slotPrice}</a>
			</div>`;
	}

	clearTable() {
		for (const child of this._wrapper.children) {
			child.remove();
		}
		this.paginationSelector?.classList.add(ClassNames.Hidden);
		this._notAvailableWrapper.classList.remove(ClassNames.Hidden);
	}

	clearNotAvailableState() {
		this.paginationSelector?.classList.remove(ClassNames.Hidden);
		this._notAvailableWrapper.classList.add(ClassNames.Hidden);
	}
}
export default CourtAvailability;
